import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/process", label: "Process" },
  { to: "/contact", label: "Contact" },
];

export function Logo({ className = "" }) {
  const site = useSite();
  const logo = site?.data?.settings?.logo;
  const name = site?.data?.settings?.company_name || "RAHI DEV";
  return (
    <Link to="/" className={`flex items-center gap-2.5 ${className}`} data-testid="site-logo">
      {logo ? <img src={logo.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${logo}` : logo} alt="" className="h-8 w-8 object-contain" /> :
        <span className="grid h-7 w-7 place-items-center rounded-sm bg-[var(--accent)] font-display text-[13px] font-extrabold text-white">R</span>}
      <span className="font-display text-[15px] font-bold tracking-[0.14em] text-white">{name}</span>
    </Link>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || open ? "glass border-b" : "border-b border-transparent"}`} data-testid="public-navbar">
      <div className="container-x flex h-[72px] items-center justify-between">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) => `text-sm font-medium transition-colors duration-200 hover:text-white ${isActive ? "text-white" : "text-[var(--text-dim)]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Link to={user?.role === "client" ? "/client" : "/login"} className="text-sm font-medium text-[var(--text-dim)] transition-colors hover:text-white" data-testid="nav-portal-link">
            {user?.role === "client" ? "My Portal" : "Client Login"}
          </Link>
          <Link to="/contact" className="btn-primary !py-2.5 !px-5" data-testid="nav-cta">Start a Project <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <button className="grid h-10 w-10 place-items-center rounded-full border border-[var(--line-strong)] text-white lg:hidden" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={open} data-testid="nav-mobile-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="glass fixed inset-x-0 top-[72px] bottom-0 overflow-y-auto border-t lg:hidden" data-testid="nav-mobile-menu">
          <div className="container-x flex flex-col gap-1 py-6">
            {NAV.map((n, i) => (
              <NavLink key={n.to} to={n.to} className="rise flex items-center justify-between border-b hairline py-4 font-display text-2xl font-semibold uppercase" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}>
                {n.label}<span className="eyebrow">0{i + 1}</span>
              </NavLink>
            ))}
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/contact" className="btn-primary justify-center" data-testid="mobile-nav-cta">Start a Project <ArrowUpRight className="h-4 w-4" /></Link>
              <Link to={user?.role === "client" ? "/client" : "/login"} className="btn-ghost justify-center" data-testid="mobile-nav-portal">{user?.role === "client" ? "My Portal" : "Client Login"}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const site = useSite();
  const s = site?.data?.settings || {};
  const f = site?.data?.pages?.footer || {};
  const socials = Object.entries(s.social_links || {}).filter(([, v]) => v);
  return (
    <footer className="relative border-t hairline" data-testid="public-footer">
      <div className="container-x grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-5">
          <Logo />
          <p className="mt-6 max-w-sm text-sm text-[var(--text-dim)]">{f.tagline}</p>
          <p className="mt-8 font-display text-3xl font-bold uppercase leading-none text-white sm:text-4xl">Let's build<br />something that matters.</p>
          <Link to="/contact" className="btn-primary mt-8" data-testid="footer-cta">Start a Project <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7 lg:pl-12">
          <div>
            <p className="eyebrow mb-5">Company</p>
            <ul className="space-y-3 text-sm">
              {[["/work", "Work"], ["/services", "Services"], ["/about", "About"], ["/team", "Team"], ["/process", "Process"], ["/contact", "Contact"]].map(([to, l]) => (
                <li key={to}><Link to={to} className="text-[var(--text-dim)] transition-colors hover:text-white">{l}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-5">Contact</p>
            <ul className="space-y-3 text-sm text-[var(--text-dim)]">
              {s.email && <li><a href={`mailto:${s.email}`} className="hover:text-white">{s.email}</a></li>}
              {s.phone && <li>{s.phone}</li>}
              {s.whatsapp && <li>WhatsApp: {s.whatsapp}</li>}
              {s.address && <li>{s.address}</li>}
              {!s.email && !s.phone && <li>Use the project inquiry form.</li>}
              <li><Link to="/login" className="hover:text-white" data-testid="footer-login">Client Login</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-5">Social</p>
            <ul className="space-y-3 text-sm text-[var(--text-dim)]">
              {socials.length ? socials.map(([k, v]) => <li key={k}><a href={v} target="_blank" rel="noreferrer" className="capitalize hover:text-white">{k}</a></li>) : <li className="text-[var(--text-mute)]">Coming soon</li>}
            </ul>
            <p className="eyebrow mb-5 mt-8">Legal</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/privacy" className="text-[var(--text-dim)] hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-[var(--text-dim)] hover:text-white">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t hairline">
        <div className="container-x flex flex-col gap-2 py-6 text-xs text-[var(--text-mute)] sm:flex-row sm:items-center sm:justify-between">
          <span data-testid="footer-copyright">{f.copyright}</span>
          <span className="font-mono uppercase tracking-[0.2em]">CEO · {s.ceo_name}</span>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return (
    <div className="relative min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <Navbar />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}
